import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './crm.module.css';

interface Props {
  /** The element the popover anchors to (its bottom edge). Null → nothing renders. */
  anchorEl: HTMLElement | null;
  onClose: () => void;
  children: ReactNode;
  /** Horizontal alignment relative to the anchor. Default 'left' (menu's left edge under anchor's left). */
  align?: 'left' | 'right';
}

/** Where focus goes on open, most-specific first: a menu item, then any focusable control, then the
 *  popover itself (it carries tabIndex={-1}) so a body with no controls at all — the Columns panel's
 *  ⓘ explainer, which is a title, a definition sentence and a chip — is still reachable and read out
 *  rather than being keyboard- and screen-reader-invisible behind a trigger. */
const FOCUS_TARGETS = '[role="menuitem"], button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * A lightweight anchored menu popover, portaled to <body> so it escapes overflow:hidden containers
 * (the fixed grid shell). Closes on outside-mousedown, Escape, and any scroll/resize. Positioned from
 * the anchor's rect, and FLIPS above the anchor when there isn't room below (so a menu anchored near
 * the viewport bottom stays reachable). Focus moves into the popover on open and ArrowUp/Down cycle
 * the items (role="menu" keyboard model). Menu items should carry role="menuitem".
 *
 * On close, focus returns to `anchorEl` — the trigger, at every call site — but ONLY when the popover
 * still owned focus (or nothing does). That guard is what keeps the return from stealing focus from a
 * control an item deliberately opened: "Rename view" closes the menu and autofocuses the rename
 * input in the same commit, and yanking focus back to the ⋯ button afterwards would undo it.
 */
export function CrmPopover({ anchorEl, onClose, children, align = 'left' }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<CSSProperties | null>(null);

  useEffect(() => {
    if (!anchorEl) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current && !ref.current.contains(t) && !anchorEl.contains(t)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"], button') ?? []);
        if (!items.length) return;
        e.preventDefault();
        const idx = items.indexOf(document.activeElement as HTMLElement);
        const n = e.key === 'ArrowDown' ? (idx + 1) % items.length : (idx - 1 + items.length) % items.length;
        items[n]?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    // capture=true so an inner scroll container (the grid body) also triggers the close
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [anchorEl, onClose]);

  // after mount: flip up if the menu would overflow the bottom, clamp within the viewport, and move
  // focus into the menu. useLayoutEffect runs before paint so there's no visible reposition jump.
  useLayoutEffect(() => {
    const menu = ref.current;
    if (!anchorEl || !menu) return;
    const rect = anchorEl.getBoundingClientRect();
    const mh = menu.offsetHeight;
    const mw = menu.offsetWidth;
    const spaceBelow = window.innerHeight - rect.bottom;
    const style: CSSProperties = { position: 'fixed', zIndex: 1200 };
    if (spaceBelow < mh + 8 && rect.top > spaceBelow) {
      style.bottom = Math.round(window.innerHeight - rect.top + 4); // flip above
    } else {
      style.top = Math.round(rect.bottom + 4);
    }
    if (align === 'right') style.right = Math.round(Math.max(8, window.innerWidth - rect.right));
    else style.left = Math.round(Math.max(8, Math.min(rect.left, window.innerWidth - mw - 8)));
    setPos(style);
    (menu.querySelector<HTMLElement>(FOCUS_TARGETS) ?? menu).focus();
  }, [anchorEl, align]);

  // Focus return. Separate effect so it is keyed on `anchorEl` alone — the positioning effect above
  // also re-runs on `align`, and a cleanup there would fire on a re-render rather than only on close.
  useEffect(() => {
    if (!anchorEl) return;
    const menu = ref.current;
    return () => {
      const active = document.activeElement;
      // `menu` is already detached by the time this passive cleanup runs, so the usual case is
      // `document.body`; the `contains` arm covers a synchronous close while focus is still inside.
      if (active === document.body || active === null || menu?.contains(active)) {
        anchorEl.focus({ preventScroll: true });
      }
    };
  }, [anchorEl]);

  if (!anchorEl) return null;
  // first-paint fallback position (down) before the layout effect refines/flips it
  const rect = anchorEl.getBoundingClientRect();
  const style: CSSProperties = pos ?? {
    position: 'fixed',
    zIndex: 1200,
    top: Math.round(rect.bottom + 4),
    ...(align === 'right'
      ? { right: Math.round(Math.max(8, window.innerWidth - rect.right)) }
      : { left: Math.round(rect.left) }),
  };
  return createPortal(
    <div ref={ref} className={styles.popover} style={style} role="menu" tabIndex={-1}>
      {children}
    </div>,
    document.body,
  );
}
